import React, { useMemo, useEffect, useRef, useState } from 'react';
import { IconState } from '../../../../components/IconState';
import IntradayDetailLayout from './IntradayDetailLayout';
import { useExcludedDays } from '../../../../contexts/ExcludedDaysContext';
import { EXCLUDED_HATCH_CLASS } from '../../../../utils/excludedDays';
import AnalyticsHero from '../../../../components/analytics/AnalyticsHero';
import AnalyticsStatStrip from '../../../../components/analytics/AnalyticsStatStrip';
import DistributionStripeCards from '../../../../components/analytics/DistributionStripeCards';
import PeerLeaderboardRail from '../../../../components/analytics/PeerLeaderboardRail';
import useAnalyticsTense from '../../../../components/analytics/useAnalyticsTense';
import useDelayedFlag from '../../../../components/analytics/useDelayedFlag';
import { rankingHero } from '../../../../components/analytics/heroTemplates';
import { getDayInResourceTz } from '../../../../utils/datetime';
import {
  ChartBarSquareIcon,
  UserIcon,
} from '@heroicons/react/24/outline';

export default function RankingsReport({
  forecastType,
  setForecastType,
  selectedUser,
  setSelectedUser,
  selectedYear,
  setSelectedYear,
  selectedMonthNum,
  setSelectedMonthNum,
  participants,
  participantsLoading,
  monthOptions,
  yearOptions,
  monthlyStatsData,
  monthlyStatsLoading,
  monthlyStatsError,
  monthOverMonthData,
  monthOverMonthLoading,
  monthOverMonthError,
  horizonOverviewData,
  horizonOverviewLoading,
  horizonOverviewError,
  selectedHorizon,
  onSelectHorizon,
  availableHorizons = [],
  gateClosureData,
  gateClosureLoading,
  selectedGateHour = null,
  onSelectGateHour,
  gateHourDetailData = null,
  gateHourDetailLoading = false,
  hideUserSelector = false,
  blurSensitive = false,
  onDayClick,
  dayClickDisabledHint,
  onSelectForecaster = null,
}) {
  const [displayedHorizon, setDisplayedHorizon] = useState(selectedHorizon);
  // Outage days of this horizon: hatched in the daily grid, never ranked.
  const excludedDays = useExcludedDays(displayedHorizon, selectedYear, selectedMonthNum);
  const [isViewTransitioning, setIsViewTransitioning] = useState(false);
  const prevHorizonRef = useRef(selectedHorizon);
  const transitionTimersRef = useRef({ swap: null, fadeIn: null });

  useEffect(() => {
    if (prevHorizonRef.current === selectedHorizon) return;
    prevHorizonRef.current = selectedHorizon;

    // Cancel any pending transition from a prior horizon switch.
    const timers = transitionTimersRef.current;
    if (timers.swap) window.clearTimeout(timers.swap);
    if (timers.fadeIn) window.clearTimeout(timers.fadeIn);

    setIsViewTransitioning(true);
    timers.swap = window.setTimeout(() => {
      // Swap content while fully faded out, then release opacity after the
      // DOM commits so the fade-in actually animates.
      setDisplayedHorizon(selectedHorizon);
      timers.fadeIn = window.setTimeout(() => {
        setIsViewTransitioning(false);
      }, 20);
    }, 200);
  }, [selectedHorizon]);

  useEffect(() => () => {
    const timers = transitionTimersRef.current;
    if (timers.swap) window.clearTimeout(timers.swap);
    if (timers.fadeIn) window.clearTimeout(timers.fadeIn);
  }, []);

  // Calculate consecutive days in top 10 (counting backwards from most recent day)
  const consecutiveTop10Days = useMemo(() => {
    if (!monthlyStatsData?.daily_ranks_json || monthlyStatsData.daily_ranks_json.length === 0) {
      return 0;
    }

    const dailyRanks = monthlyStatsData.daily_ranks_json;
    let consecutiveDays = 0;

    // Iterate backwards from the most recent day
    for (let i = dailyRanks.length - 1; i >= 0; i--) {
      const rank = dailyRanks[i].rank;

      // Count if rank is valid and within top 10
      if (rank !== null && rank <= 10) {
        consecutiveDays++;
      } else {
        // Stop counting at first day outside top 10 or with no data
        break;
      }
    }

    return consecutiveDays;
  }, [monthlyStatsData]);

  // Editorial-layout props (hero, stat strip, distribution, peer rail).
  const monthLabel = useMemo(
    () => monthOptions.find((m) => m.value === selectedMonthNum)?.label || '',
    [monthOptions, selectedMonthNum],
  );
  const tense = useAnalyticsTense({ year: selectedYear, month: selectedMonthNum, monthLabel });

  const selfUser = useMemo(() => {
    if (!Array.isArray(participants) || !selectedUser) return null;
    return participants.find((p) => p.user === selectedUser) || null;
  }, [participants, selectedUser]);
  const userDisplayName = blurSensitive
    ? (selfUser?.user_username || selfUser?.user_email || '')
    : (selfUser?.user_email || selfUser?.user_username || '');

  // Peer leaderboard rows. Includes EVERY forecaster the engine emitted.
  // Sorted by `field_win_rate` (higher = better) — each peer's pairwise win
  // count across all peers they share submission days with. Single canonical
  // ordering shared across viewers; per-row "vs. you" is computed locally
  // inside the rail from `daily_scores`.
  const peerRows = useMemo(() => {
    const raw = monthlyStatsData?.month_scores_json;
    if (!Array.isArray(raw)) return [];
    return [...raw]
      .filter((row) => row && row.user_id != null)
      .sort((a, b) => {
        const aSort = a.field_win_rate == null ? -Infinity : Number(a.field_win_rate);
        const bSort = b.field_win_rate == null ? -Infinity : Number(b.field_win_rate);
        return bSort - aSort;
      })
      .map((row, idx) => ({
        ...row,
        score: row.field_win_rate != null ? Number(row.field_win_rate) : null,
        rank: idx + 1,
        delta: 0,
      }));
  }, [monthlyStatsData]);

  // The logged-in user's row in the canonical leaderboard. Used by hero
  // (position rank) and the AVG DAILY RANK strip cell (same-day-fair avg).
  const selfPeerRow = useMemo(
    () => peerRows.find((r) => r.user_id === selectedUser) || null,
    [peerRows, selectedUser],
  );

  const heroProps = useMemo(
    () =>
      rankingHero({
        user: userDisplayName,
        monthLabel,
        year: selectedYear,
        // Hero shows the user's POSITION in the field-win-rate leaderboard
        // — the canonical "how you compare to the rest of the field" sort.
        rank: selfPeerRow?.rank ?? null,
        total: monthlyStatsData?.nr_participants,
        streak: consecutiveTop10Days,
        submitted: monthlyStatsData?.days_with_submissions,
        daysInPeriod: monthlyStatsData?.days_in_period,
        tense: tense.tense,
        periodPhrase: tense.periodPhrase,
      }),
    [
      userDisplayName,
      monthLabel,
      selectedYear,
      monthlyStatsData,
      selfPeerRow,
      consecutiveTop10Days,
      tense.tense,
      tense.periodPhrase,
    ],
  );

  const stripCells = useMemo(() => {
    // Avg daily rank (over the user's submitted days) is read from the
    // per-row payload — the rail row carries the float; top-level field is
    // truncated to int.
    const avgRank = selfPeerRow?.avg_rank ?? monthlyStatsData?.avg_rank;
    const fieldWinRate = selfPeerRow?.field_win_rate;
    const fieldRank = selfPeerRow?.rank;
    const total = monthlyStatsData?.nr_participants;
    const days = monthlyStatsData?.days_with_submissions;
    const totalDays = monthlyStatsData?.days_in_period;
    const part = monthlyStatsData?.participation_rate;
    const delta = monthOverMonthData?.avg_rank_comparison?.difference;

    const deltaCell = (() => {
      if (delta == null) return { value: '—', subtext: 'No prior data' };
      // API sends difference = current - previous. For ranks, smaller is better,
      // so a negative difference means the rank dropped (improved).
      if (Math.abs(delta) < 0.005) return { value: '0.00', subtext: 'Stable' };
      const improved = delta < 0;
      const sign = delta > 0 ? '+' : '−';
      return {
        value: (
          <span className={improved ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
            {sign}{Math.abs(delta).toFixed(2)}
          </span>
        ),
        subtext: improved ? 'Improved' : 'Worse',
      };
    })();

    return [
      {
        label: 'Avg Daily Rank',
        value: <span className="text-brand-600 dark:text-brand-400">#{avgRank != null ? Number(avgRank).toFixed(2) : '—'}</span>,
        subtext: total != null ? (
          <>
            of {total} · across your submitted days
          </>
        ) : '',
      },
      {
        label: 'Field Win Rate',
        value: (
          <span className="text-brand-600 dark:text-brand-400">
            {fieldWinRate != null ? `${Math.round(Number(fieldWinRate) * 100)}%` : '—'}
          </span>
        ),
        subtext: fieldRank != null && total != null
          ? `#${fieldRank} of ${total} this month`
          : 'No co-submitted days',
      },
      {
        label: 'Submitted',
        value: days != null && totalDays != null ? `${days}/${totalDays}` : '—',
        subtext: part != null ? `${Number(part).toFixed(0)}% participation` : '',
      },
      {
        label: 'Top-10 Streak',
        value: (
          <span className={consecutiveTop10Days >= 5 ? 'text-amber-600 dark:text-amber-400' : ''}>
            {consecutiveTop10Days}
          </span>
        ),
        subtext: 'Consecutive days',
      },
      {
        label: 'vs Last Month',
        value: deltaCell.value,
        subtext: deltaCell.subtext,
      },
    ];
  }, [monthlyStatsData, monthOverMonthData, consecutiveTop10Days, selfPeerRow]);

  const distributionCards = useMemo(() => {
    const dist = monthlyStatsData?.rank_distribution_json || {};
    const total = (dist.rank_1_5 || 0) + (dist.rank_6_10 || 0) + (dist.rank_11_plus || 0);
    const pct = (n) => (total > 0 ? Math.round((n / total) * 100) : 0);

    // Per-rank histogram from the raw daily-rank timeline. The aggregated
    // rank_distribution_json only carries the three coarse bins; we need
    // single-rank granularity for the inline mini-bar groups.
    const rankCounts = new Map();
    (monthlyStatsData?.daily_ranks_json || []).forEach((d) => {
      if (d && typeof d.rank === 'number') {
        rankCounts.set(d.rank, (rankCounts.get(d.rank) || 0) + 1);
      }
    });
    const rangeBars = (start, end) =>
      Array.from({ length: end - start + 1 }, (_, i) => {
        const r = start + i;
        return { label: `#${r}`, count: rankCounts.get(r) || 0 };
      });
    // 11–14 individually, then a single "15+" catch-all so the long tail
    // doesn't disappear when the user occasionally lands far down the field.
    const tailBars = () => {
      const bars = rangeBars(11, 14);
      let tail = 0;
      rankCounts.forEach((c, r) => { if (r >= 15) tail += c; });
      bars.push({ label: '15+', count: tail });
      return bars;
    };

    const subFor = (n) => {
      if (total === 0) return 'no data';
      return n > 0 ? `${pct(n)}% of period` : 'no sessions';
    };

    return [
      { accentColor: 'green',  label: 'Ranks 1–5',  count: dist.rank_1_5 || 0,     subtext: subFor(dist.rank_1_5 || 0),     bars: rangeBars(1, 5)  },
      { accentColor: 'indigo', label: 'Ranks 6–10', count: dist.rank_6_10 || 0,    subtext: subFor(dist.rank_6_10 || 0),    bars: rangeBars(6, 10) },
      { accentColor: 'amber',  label: 'Ranks 11+',  count: dist.rank_11_plus || 0, subtext: subFor(dist.rank_11_plus || 0), bars: tailBars()       },
    ];
  }, [monthlyStatsData]);

  const maxMissedDays = monthlyStatsData?.max_missed_days ?? 5;

  // Debounce the loading skeletons so fast loads (e.g. navigating in with
  // cached data, or a quick refetch) don't flash a skeleton for a few frames.
  // Only surface the skeleton if the load genuinely takes a moment.
  const showPreSelectSkeleton = useDelayedFlag(participantsLoading);
  const showStatsShell = useDelayedFlag(monthlyStatsLoading && !monthlyStatsData);

  // Horizon offset can be 0 (Intraday), so avoid falsy checks.
  // While the parent is auto-selecting the first horizon we may briefly
  // see a null displayedHorizon — render a loading shell in that case.
  const isHorizonPending = displayedHorizon === null || displayedHorizon === undefined;
  const isIntradayDetail = displayedHorizon === 0;
  const horizonLabel = isHorizonPending
    ? null
    : displayedHorizon === 0
      ? 'Intraday'
      : `D+${displayedHorizon}`;

  // The parent resets the horizon to null on mount and re-picks the first
  // one once the overview lands, so `isHorizonPending` is briefly true on
  // every navigation in. Debounce its loading shell too, so it doesn't flash.
  const showHorizonShell = useDelayedFlag(isHorizonPending);

  return (
    <section className="py-4 sm:py-6">

      {/* No user selected yet. While participants/active-users are still
          loading (the initial fetch waterfall, before the auto-pick lands),
          show a skeleton instead of the empty state so the admin sees the
          page filling in rather than a blank "Select a User" flash. */}
      {!selectedUser && !hideUserSelector ? (
        participantsLoading ? (
          showPreSelectSkeleton ? (
            <div className="space-y-6 animate-pulse py-2">
              <div className="h-8 w-2/3 rounded bg-gray-200 dark:bg-gray-700" />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-24 rounded-lg border border-gray-300 dark:border-gray-600" />
                ))}
              </div>
              <div className="h-72 rounded-lg border border-gray-300 dark:border-gray-600" />
            </div>
          ) : null
        ) : (
          <IconState
            icon={UserIcon}
            title="Select a User"
            description="Please select a user from the dropdown above to view their ranking analytics."
            dashedBorder={true}
          />
        )
      ) : (
        <div
          className={`transition-opacity duration-200 ease-in-out ${
            isViewTransitioning ? 'opacity-0' : 'opacity-100'
          }`}
        >
          {isHorizonPending ? (
            horizonOverviewLoading
              || !Array.isArray(horizonOverviewData)
              || horizonOverviewData.length > 0 ? (
              /* ── Loading shell while parent auto-selects the first horizon.
                   Debounced so a quick auto-pick doesn't flash it. ── */
              showHorizonShell ? (
                <div className="space-y-6 animate-pulse">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="h-24 rounded-lg border border-gray-300 dark:border-gray-600" />
                    ))}
                  </div>
                  <div className="h-72 rounded-lg border border-gray-300 dark:border-gray-600" />
                </div>
              ) : null
            ) : (
              <IconState
                icon={ChartBarSquareIcon}
                title="No Data Available"
                description="No ranking data is available for the selected period. This may happen when no observations exist or when no forecasts were submitted."
                dashedBorder={true}
              />
            )
          ) : isIntradayDetail ? (
            /* ── Intraday detail mode: day-ahead structural shell with kept Intraday charts ── */
            <div className="view-enter">
            <IntradayDetailLayout
              mode="rankings"
              heroProps={heroProps}
              horizonLabel={horizonLabel}
              stripCells={stripCells}
              gateClosureData={gateClosureData}
              gateClosureLoading={gateClosureLoading}
              monthlyStatsData={monthlyStatsData}
              monthlyStatsLoading={monthlyStatsLoading}
              selectedYear={selectedYear}
              selectedMonth={selectedMonthNum}
              monthLabel={monthLabel}
              onDayClick={onDayClick}
              dayClickDisabledHint={dayClickDisabledHint}
              distributionCards={distributionCards}
              peerRows={peerRows}
              selfUserId={selectedUser}
              maxMissedDays={maxMissedDays}
              blurSensitive={blurSensitive}
              metricLabel={forecastType === 'probabilistic' ? 'Winkler' : 'RMSE'}
              onSelectForecaster={onSelectForecaster}
            />
            </div>
          ) : (
            /* ── Detail mode: drill-down content (day-ahead horizons) ── */
            <>
              {monthlyStatsLoading && !monthlyStatsData ? (
                showStatsShell ? (
                  <div className="space-y-6 animate-pulse">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[...Array(4)].map((_, i) => (
                        <div key={i} className="h-24 rounded-lg border border-gray-300 dark:border-gray-600" />
                      ))}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="h-72 rounded-lg border border-gray-300 dark:border-gray-600" />
                      <div className="h-72 rounded-lg border border-gray-300 dark:border-gray-600" />
                    </div>
                    <div className="h-48 rounded-lg border border-gray-300 dark:border-gray-600" />
                  </div>
                ) : null
              ) : monthlyStatsData && monthlyStatsData.year == null ? (
                <IconState
                  icon={ChartBarSquareIcon}
                  title="No Metrics Available"
                  description="No performance data is available for the selected horizon and period."
                  dashedBorder={true}
                />
              ) : (
                <div className="view-enter">
                  <AnalyticsHero
                    eyebrow={heroProps.eyebrow}
                    headline={heroProps.headline}
                    lede={heroProps.lede}
                    horizonLabel={horizonLabel}
                  />

                  <AnalyticsStatStrip cells={stripCells} className="mb-4" />

                  {/* Daily Ranks — header sits at section level, card holds only the heatmap */}
                  <section className="mb-4">
                    <header className="mb-2">
                      <div className="text-[11px] uppercase tracking-[0.22em] font-medium text-brand-600 dark:text-brand-400">
                        Daily Ranks
                      </div>
                      <h3 className="mt-1.5 text-base font-semibold tracking-tight text-slate-900 dark:text-white">
                        Your daily ranks for {monthLabel} {selectedYear}
                      </h3>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        One cell per day. Lower rank wins.
                      </p>
                    </header>
                    <div className="bg-surface dark:bg-gray-870 rounded-xl p-5 shadow-sm border border-gray-200/70 dark:border-gray-600/70">
                    {(!monthlyStatsData?.daily_ranks_json || monthlyStatsData.daily_ranks_json.length === 0)
                      && !(monthlyStatsData?.daily_scores_json || []).some((d) => d && d.is_penalty && d.target_day) ? (
                      <div className="h-28 rounded-md bg-surface dark:bg-gray-870 border border-gray-200/70 dark:border-gray-600/70 flex items-center justify-center">
                        <div className="text-sm text-gray-500">No data available for selected month</div>
                      </div>
                    ) : (
                      (() => {
                        const getColorForRankTier = (rank) => {
                          if (rank == null) return 'border border-gray-300 dark:border-gray-600 border-gray-300 dark:border-gray-600';
                          if (rank <= 5)  return 'bg-green-500 border-green-600 dark:bg-green-400 dark:border-green-500';
                          if (rank <= 10) return 'bg-brand-500 border-brand-600 dark:bg-brand-400 dark:border-brand-500';
                          return 'bg-amber-500 border-amber-600 dark:bg-amber-400 dark:border-amber-500';
                        };
                        // Active day the user didn't qualify on (penalty day).
                        const NQ_TILE_CLASS = 'bg-red-100 border-red-300 dark:bg-red-500/25 dark:border-red-500/50';

                        const dailyRanksJson = monthlyStatsData?.daily_ranks_json || [];
                        const daysInMonth = new Date(selectedYear, selectedMonthNum, 0).getDate();
                        const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
                        const rankByDay = {};
                        const dateByDay = {};
                        dailyRanksJson.forEach((d) => {
                          const day = getDayInResourceTz(d.target_day);
                          if (day == null || excludedDays.has(d.target_day)) return;
                          rankByDay[day] = d.rank;
                          dateByDay[day] = d.target_day;
                        });

                        // Penalty days carry a real target_day but no rank — the
                        // rank series omits them. Render as clickable "NQ".
                        const missedByDay = {};
                        (monthlyStatsData?.daily_scores_json || []).forEach((d) => {
                          if (!d || !d.is_penalty || !d.target_day) return;
                          if (excludedDays.has(d.target_day)) return;
                          const day = getDayInResourceTz(d.target_day);
                          if (day == null || rankByDay[day] != null) return;
                          missedByDay[day] = d.target_day;
                        });

                        // Day of month -> reason of the excluded (hatched) days.
                        const excludedByDay = new Map([...excludedDays].map(
                          ([isoDay, reason]) => [Number(isoDay.slice(8)), reason]));

                        return (
                          <div className="space-y-3">
                            <div
                              className="grid"
                              style={{
                                gridTemplateColumns: `repeat(${daysInMonth}, minmax(10px, 1fr))`,
                                gridTemplateRows: 'auto 22px',
                                gap: '2px',
                              }}
                            >
                              {days.map((day) => (
                                <div
                                  key={`hdr-${day}`}
                                  className="text-[9px] text-center font-mono tabular-nums text-gray-400 dark:text-gray-500 leading-tight pb-[2px]"
                                >
                                  {day}
                                </div>
                              ))}
                              {days.map((day) => {
                                const rank = rankByDay[day];
                                const hasData = day in rankByDay && rank != null;
                                const isNq = !hasData && day in missedByDay;
                                const dateStr = dateByDay[day] || (isNq ? missedByDay[day] : undefined);
                                const excludedReason = excludedByDay.get(day);
                                const clickable = onDayClick && dateStr && !excludedReason;
                                const Tag = clickable ? 'button' : 'div';
                                let baseTitle = hasData
                                  ? `Day ${day}: Rank #${rank}`
                                  : (isNq ? `Day ${day}: Not qualified` : `Day ${day}: No data`);
                                if (excludedReason) baseTitle = `Day ${day}: Excluded · ${excludedReason}`;
                                const tooltipSuffix = clickable
                                  ? ' · click for drill-down'
                                  : (dayClickDisabledHint ? ` · ${dayClickDisabledHint}` : '');
                                let tileClass = hasData
                                  ? getColorForRankTier(rank)
                                  : (isNq ? NQ_TILE_CLASS : getColorForRankTier(null));
                                if (excludedReason) {
                                  tileClass = `border-gray-300 dark:border-gray-600 ${EXCLUDED_HATCH_CLASS}`;
                                }
                                return (
                                  <Tag
                                    key={`cell-${day}`}
                                    type={clickable ? 'button' : undefined}
                                    onClick={clickable ? () => onDayClick(dateStr, displayedHorizon) : undefined}
                                    className={`rounded-md border relative group ${tileClass} ${
                                      clickable
                                        ? 'cursor-pointer hover:ring-2 hover:ring-brand-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500'
                                        : 'cursor-default'
                                    }`}
                                    style={{ height: '22px', padding: 0 }}
                                    title={baseTitle + tooltipSuffix}
                                  >
                                    {hasData && (
                                      <span
                                        className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white font-mono tabular-nums leading-none pointer-events-none"
                                        style={{ textShadow: '0 1px 1px rgba(0,0,0,0.35)' }}
                                      >
                                        {rank}
                                      </span>
                                    )}
                                    {isNq && (
                                      <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-red-600 dark:text-red-300 leading-none pointer-events-none">
                                        NQ
                                      </span>
                                    )}
                                    <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10 pointer-events-none">
                                      {baseTitle}{tooltipSuffix}
                                    </div>
                                  </Tag>
                                );
                              })}
                            </div>

                            {/* Legend */}
                            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                              <div className="flex items-center gap-1">
                                <span className="h-3 w-3 rounded-sm bg-green-500 border border-green-600 inline-block" />
                                Top 5
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="h-3 w-3 rounded-sm bg-brand-500 border border-brand-600 inline-block" />
                                Top 6–10
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="h-3 w-3 rounded-sm bg-amber-500 border border-amber-600 inline-block" />
                                Other
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="h-3 w-3 rounded-sm bg-red-100 border border-red-300 dark:bg-red-500/25 dark:border-red-500/50 inline-block" />
                                Not qualified
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="h-3 w-3 rounded-sm border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 inline-block" />
                                No data
                              </div>
                              {excludedByDay.size > 0 && (
                                <div className="flex items-center gap-1">
                                  <span className={`h-3 w-3 rounded-sm border border-gray-300 dark:border-gray-600 inline-block ${EXCLUDED_HATCH_CLASS}`} />
                                  Excluded
                                </div>
                              )}
                              <span className="ml-auto text-gray-400">
                                {dailyRanksJson.filter(d => d.rank != null && !excludedDays.has(d.target_day)).length} days with data
                              </span>
                            </div>
                          </div>
                        );
                      })()
                    )}
                    </div>
                  </section>

                  <div className="mb-4 grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
                    <DistributionStripeCards
                      orientation="vertical"
                      eyebrow="Distribution"
                      headline="Where your sessions landed"
                      description="How often you finished in each rank band over this period."
                      cards={distributionCards}
                    />

                    <PeerLeaderboardRail
                      rows={peerRows}
                      selfUserId={selectedUser}
                      mode="rankings"
                      metricLabel="Avg Rank"
                      maxMissedDays={maxMissedDays}
                      blurSensitive={blurSensitive}
                      onSelectForecaster={onSelectForecaster}
                    />
                  </div>

                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
